import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const bgGradients = {
  warning: 'from-amber-500 via-orange-500 to-rose-500',
  danger: 'from-rose-500 via-red-500 to-red-700',
  info: 'from-blue-600 via-indigo-600 to-purple-600',
  success: 'from-emerald-500 via-teal-500 to-cyan-500'
};

const iconBackgrounds = {
  warning: 'bg-white/20 text-white',
  danger: 'bg-white/20 text-white',
  info: 'bg-white/20 text-white',
  success: 'bg-white/20 text-white'
};

export default function ConfirmActionDialog({
  isOpen,
  onCancel,
  onConfirm,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'danger',
  showSecondaryAction = false,
  secondaryLabel = 'Send Email',
  onSecondary,
  loading = false,
  icon,
  iconClassName = '',
  children
}) {
  if (!isOpen) return null;

  const gradientClass = bgGradients[confirmVariant] || bgGradients.danger;
  const iconBgClass = iconBackgrounds[confirmVariant] || iconBackgrounds.danger;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
        <div className={`relative bg-gradient-to-r ${gradientClass} p-6 text-white`}> 
          <button
            onClick={onCancel}
            className="absolute right-4 top-4 text-white/70 transition hover:text-white"
            aria-label="Close confirmation dialog"
            disabled={loading}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-4">
            <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${iconBgClass}`}>
              {icon ? (
                React.cloneElement(icon, { className: `h-6 w-6 ${icon.props.className || ''} ${iconClassName}` })
              ) : (
                <span className="text-lg font-bold">!</span>
              )}
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-white/60">Confirm Action</p>
              <h2 className="mt-1 text-xl font-semibold leading-tight">{title}</h2>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-6">
          {description && (
            <p className="text-sm text-gray-600">
              {description}
            </p>
          )}

          {children && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              {children}
            </div>
          )}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
              disabled={loading}
            >
              {cancelLabel}
            </button>

            {showSecondaryAction && (
              <button
                type="button"
                onClick={onSecondary}
                className="inline-flex items-center justify-center rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 transition hover:bg-gray-100 disabled:opacity-60"
                disabled={loading}
              >
                {secondaryLabel}
              </button>
            )}

            <button
              type="button"
              onClick={onConfirm}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:opacity-60 ${
                confirmVariant === 'danger'
                  ? 'bg-rose-600 hover:bg-rose-500'
                  : confirmVariant === 'warning'
                  ? 'bg-amber-600 hover:bg-amber-500'
                  : confirmVariant === 'success'
                  ? 'bg-emerald-600 hover:bg-emerald-500'
                  : 'bg-indigo-600 hover:bg-indigo-500'
              }`}
              disabled={loading}
            >
              {loading ? 'Processing…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


